import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ZiweiChart, 
  DetailedFortune, 
  YearlyFortune, 
  getDetailedFortune, 
  calculateYearlyFortune 
} from '@/lib/ziwei-calculator';
import { 
  BookOpen, 
  Star, 
  TrendingUp, 
  Calendar,
  Heart,
  Home,
  Briefcase,
  DollarSign,
  Activity,
  GraduationCap,
  Users,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';

interface DetailedInterpretationPanelProps {
  chart: ZiweiChart;
}

const DetailedInterpretationPanel: React.FC<DetailedInterpretationPanelProps> = ({ chart }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearlyFortune, setYearlyFortune] = useState<YearlyFortune | null>(null);
  
  const detailedFortunes = getDetailedFortune(chart);
  
  // 計算年運
  const handleCalculateYearlyFortune = () => {
    const fortune = calculateYearlyFortune(chart, selectedYear);
    setYearlyFortune(fortune);
  };

  // 獲取運勢顏色
  const getTrendColor = (trend: DetailedFortune['trend']) => {
    switch (trend) {
      case 'excellent': return 'text-green-600 bg-green-100';
      case 'good': return 'text-blue-600 bg-blue-100';
      case 'average': return 'text-yellow-600 bg-yellow-100';
      case 'poor': return 'text-orange-600 bg-orange-100';
      case 'challenging': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // 獲取運勢圖標
  const getAspectIcon = (aspect: string) => {
    switch (aspect) {
      case '性格特質': return <Star className="w-5 h-5" />;
      case '事業運程': return <Briefcase className="w-5 h-5" />;
      case '財運分析': return <DollarSign className="w-5 h-5" />;
      case '感情運程': return <Heart className="w-5 h-5" />;
      case '健康運程': return <Activity className="w-5 h-5" />;
      case '家宅運程': return <Home className="w-5 h-5" />;
      case '學業運程': return <GraduationCap className="w-5 h-5" />;
      case '親情運程': return <Users className="w-5 h-5" />;
      default: return <BookOpen className="w-5 h-5" />;
    }
  };

  // 獲取分數顏色
  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-blue-600';
    if (score >= 4) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="detailed" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="detailed">詳細運程分析</TabsTrigger>
          <TabsTrigger value="yearly">流年運程推算</TabsTrigger>
        </TabsList>

        <TabsContent value="detailed" className="space-y-6">
          {/* 運程總覽 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                運程總覽
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {detailedFortunes.map((fortune, index) => (
                  <div key={index} className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      {getAspectIcon(fortune.aspect)}
                    </div>
                    <div className="text-sm font-medium">{fortune.aspect}</div>
                    <div className={`text-2xl font-bold ${getScoreColor(fortune.score)}`}>
                      {fortune.score}/10
                    </div>
                    <Badge className={`text-xs ${getTrendColor(fortune.trend)}`}>
                      {fortune.trend === 'excellent' && '極佳'}
                      {fortune.trend === 'good' && '良好'}
                      {fortune.trend === 'average' && '一般'}
                      {fortune.trend === 'poor' && '較差'}
                      {fortune.trend === 'challenging' && '挑戰'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 詳細分析 */}
          <div className="grid gap-6">
            {detailedFortunes.map((fortune, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getAspectIcon(fortune.aspect)}
                      {fortune.aspect}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${getScoreColor(fortune.score)}`}>
                        {fortune.score}/10
                      </span>
                      <Badge className={getTrendColor(fortune.trend)}>
                        {fortune.trend === 'excellent' && '極佳'}
                        {fortune.trend === 'good' && '良好'}
                        {fortune.trend === 'average' && '一般'}
                        {fortune.trend === 'poor' && '較差'}
                        {fortune.trend === 'challenging' && '挑戰'}
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* 進度條 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>運勢指數</span>
                      <span>{fortune.score * 10}%</span>
                    </div>
                    <Progress value={fortune.score * 10} className="h-2" />
                  </div>

                  {/* 相關宮位和星曜 */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">{fortune.palace}</Badge>
                    {fortune.stars.map((star, starIndex) => (
                      <Badge key={starIndex} variant="secondary" className="text-xs">
                        {star}
                      </Badge>
                    ))}
                  </div>

                  {/* 詳細描述 */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm leading-relaxed">{fortune.description}</p>
                  </div>

                  {/* 建議事項 */}
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      建議事項
                    </h4>
                    <ul className="space-y-1">
                      {fortune.suggestions.map((suggestion, suggestionIndex) => (
                        <li key={suggestionIndex} className="text-sm flex items-start gap-2">
                          <ChevronRight className="w-3 h-3 mt-0.5 text-gray-400 flex-shrink-0" />
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* 關鍵時期 */}
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
                      <Clock className="w-4 h-4 text-blue-600" />
                      關鍵時期
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {fortune.keyPeriods.map((period, periodIndex) => (
                        <Badge key={periodIndex} variant="outline" className="text-xs">
                          {period}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="yearly" className="space-y-6">
          {/* 年份選擇 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                流年運程推算
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor="year">選擇年份</Label>
                  <Input
                    id="year"
                    type="number"
                    min="2020"
                    max="2050"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <Button onClick={handleCalculateYearlyFortune} className="mt-6">
                  計算年運
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 年運結果 */}
          {yearlyFortune && (
            <div className="space-y-6">
              {/* 整體運勢 */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {yearlyFortune.year}年整體運勢 (年齡: {yearlyFortune.age}歲)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-6">
                    <div className={`text-4xl font-bold ${getScoreColor(yearlyFortune.overallScore)}`}>
                      {yearlyFortune.overallScore}/10
                    </div>
                    <div className="text-sm text-gray-600 mt-1">整體運勢評分</div>
                  </div>
                  
                  <Progress value={yearlyFortune.overallScore * 10} className="h-3 mb-4" />
                  
                  {/* 各方面運勢 */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <Briefcase className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                      <div className="text-sm">事業</div>
                      <div className={`font-bold ${getScoreColor(yearlyFortune.aspects.career)}`}>
                        {yearlyFortune.aspects.career}/10
                      </div>
                    </div>
                    <div className="text-center">
                      <DollarSign className="w-5 h-5 mx-auto mb-1 text-green-600" />
                      <div className="text-sm">財運</div>
                      <div className={`font-bold ${getScoreColor(yearlyFortune.aspects.wealth)}`}>
                        {yearlyFortune.aspects.wealth}/10
                      </div>
                    </div>
                    <div className="text-center">
                      <Activity className="w-5 h-5 mx-auto mb-1 text-red-600" />
                      <div className="text-sm">健康</div>
                      <div className={`font-bold ${getScoreColor(yearlyFortune.aspects.health)}`}>
                        {yearlyFortune.aspects.health}/10
                      </div>
                    </div>
                    <div className="text-center">
                      <Heart className="w-5 h-5 mx-auto mb-1 text-pink-600" />
                      <div className="text-sm">感情</div>
                      <div className={`font-bold ${getScoreColor(yearlyFortune.aspects.relationship)}`}>
                        {yearlyFortune.aspects.relationship}/10
                      </div>
                    </div>
                    <div className="text-center">
                      <Users className="w-5 h-5 mx-auto mb-1 text-purple-600" />
                      <div className="text-sm">家庭</div>
                      <div className={`font-bold ${getScoreColor(yearlyFortune.aspects.family)}`}>
                        {yearlyFortune.aspects.family}/10
                      </div>
                    </div>
                    <div className="text-center">
                      <GraduationCap className="w-5 h-5 mx-auto mb-1 text-indigo-600" />
                      <div className="text-sm">學業</div>
                      <div className={`font-bold ${getScoreColor(yearlyFortune.aspects.study)}`}>
                        {yearlyFortune.aspects.study}/10
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 重要事件和建議 */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* 關鍵事件 */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-600" />
                      關鍵事件
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-32">
                      <ul className="space-y-2">
                        {yearlyFortune.keyEvents.map((event, index) => (
                          <li key={index} className="text-sm flex items-start gap-2">
                            <ChevronRight className="w-3 h-3 mt-0.5 text-blue-600 flex-shrink-0" />
                            {event}
                          </li>
                        ))}
                      </ul>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* 機會與挑戰 */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      機會與挑戰
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm text-green-600 mb-2">機會</h4>
                        <ul className="space-y-1">
                          {yearlyFortune.opportunities.map((opportunity, index) => (
                            <li key={index} className="text-sm flex items-start gap-2">
                              <CheckCircle className="w-3 h-3 mt-0.5 text-green-600 flex-shrink-0" />
                              {opportunity}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {yearlyFortune.challenges.length > 0 && (
                        <div>
                          <Separator />
                          <h4 className="font-semibold text-sm text-red-600 mb-2 mt-4">挑戰</h4>
                          <ul className="space-y-1">
                            {yearlyFortune.challenges.map((challenge, index) => (
                              <li key={index} className="text-sm flex items-start gap-2">
                                <AlertCircle className="w-3 h-3 mt-0.5 text-red-600 flex-shrink-0" />
                                {challenge}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* 幸運月份 */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-600" />
                    幸運月份
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {yearlyFortune.luckyMonths.map((month, index) => (
                      <Badge key={index} className="bg-yellow-100 text-yellow-800">
                        {month}月
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    這些月份運勢較佳，適合進行重要決策或開展新計劃。
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* 注意事項 */}
      {!chart.birthInfo.hasTime && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-sm text-yellow-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              重要提醒
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-yellow-700">
              此分析因缺少確切出生時間，使用午時（12:00）計算。
              部分運程分析可能與實際情況有差異。
              建議提供準確出生時間以獲得更精確的分析結果。
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DetailedInterpretationPanel;